import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, LayoutGrid, Flame, Users, UserCog, Loader2, Share, X, PlusSquare, CreditCard,
} from 'lucide-react';
import { useClientAuth } from '../context/ClientAuthContext';
import { useLanguage } from '../context/LanguageContext';
import { TourProvider } from '../context/TourContext';
import { ClientSettingsProvider } from '../context/ClientSettingsContext';
import { useTourRegister } from '../utils/tourNav';
import { TourSpotlight } from '../components/onboarding/TourSpotlight';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL } from '../data/constants';
import { TrackingDashboard } from './tracking/TrackingDashboard';
import { CommunityHub } from './tracking/CommunityHub';
import MyWorkouts from './tracking/MyWorkouts';
import { NutritionHub } from './tracking/NutritionHub';
import { ProfilTab } from './tracking/ProfilTab';
import AICoachChat from '../components/AICoachChat';
import { InstallAppBanner } from '../components/InstallAppBanner';
import { TrialEndingBanner } from '../components/TrialEndingBanner';

// 5 onglets natifs (fusion Coaching+Communauté -> "community", Abonnement+Réglages -> "profil")
const TAB_KEYS = ['tracking', 'nutrition', 'workouts', 'community', 'profil'];
// Compat liens existants (push notifications, emails) qui pointent encore vers les anciennes clés.
const LEGACY_TAB_MAP = { social: 'community', coaching: 'community', abonnement: 'profil' };

const IOSInstallModal = ({ open, onClose, L }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="ios-install-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl p-6 max-w-sm w-full relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white" data-testid="close-ios-install-modal"><X className="w-5 h-5" /></button>
        <img src={LOGO_URL} alt="Body-Up" className="h-8 mb-4" />
        <h3 className="text-lg font-black text-white mb-3">{L("Installer l'app sur votre iPhone", 'Install the app on your iPhone')}</h3>
        <ol className="space-y-3 text-sm text-zinc-300">
          <li className="flex items-start gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary font-bold text-xs flex items-center justify-center">1</span><span>{L('Appuyez sur le bouton', 'Tap the')} <Share className="w-4 h-4 inline text-primary mx-1" /> {L("Partager en bas de Safari", 'Share button at the bottom of Safari')}</span></li>
          <li className="flex items-start gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary font-bold text-xs flex items-center justify-center">2</span><span>{L("Sélectionnez", 'Select')} <PlusSquare className="w-4 h-4 inline text-primary mx-1" /> {L("'Sur l'écran d'accueil'", "'Add to Home Screen'")}</span></li>
          <li className="flex items-start gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary font-bold text-xs flex items-center justify-center">3</span><span>{L("Confirmez avec 'Ajouter' — l'app Body-Up apparaît sur votre écran d'accueil !", "Confirm with 'Add' — the Body-Up app appears on your home screen!")}</span></li>
        </ol>
      </div>
    </div>
  );
};

const ClientDashboardPage = () => {
  const { language, toggleLanguage } = useLanguage();
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);
  const { user, loading, logout } = useClientAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { canInstall, canPromptInstall, isIOS, promptInstall } = usePWAInstall();
  const [iosModalOpen, setIosModalOpen] = useState(false);

  const handleInstallClick = async () => {
    if (canPromptInstall) await promptInstall();
    else if (isIOS) setIosModalOpen(true);
  };

  const [tab, setTab] = useState(() => {
    const raw = new URLSearchParams(location.search).get('tab');
    const resolved = LEGACY_TAB_MAP[raw] || raw;
    return TAB_KEYS.includes(resolved) ? resolved : 'tracking';
  });
  const [pendingProgramId, setPendingProgramId] = useState(null);
  const [pendingSessionLabel, setPendingSessionLabel] = useState(null);
  const [pendingCommunityView, setPendingCommunityView] = useState(() => {
    const raw = new URLSearchParams(location.search).get('tab');
    return raw === 'social' ? 'coach' : null;
  });
  const goToTab = (key, payload) => {
    const resolvedKey = LEGACY_TAB_MAP[key] || key;
    if (resolvedKey === 'workouts' && payload) {
      if (typeof payload === 'object') { setPendingProgramId(payload.programId); setPendingSessionLabel(payload.sessionLabel || null); }
      else setPendingProgramId(payload);
    }
    if (resolvedKey === 'community' && payload) setPendingCommunityView(payload);
    setTab(resolvedKey);
  };
  useTourRegister('setTab', setTab);

  useEffect(() => {
    if (!loading && !user) navigate('/espace-client', { replace: true });
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <main className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </main>
    );
  }

  const TABS = [
    { key: 'tracking', icon: Home, label: L('Mon suivi', 'My tracking'), mobileLabel: L("Aujourd'hui", 'Today') },
    { key: 'nutrition', icon: LayoutGrid, label: L('Nutrition', 'Nutrition') },
    { key: 'workouts', icon: Flame, label: L('Entraînements', 'Workouts'), mobileLabel: L('Sport', 'Workouts') },
    { key: 'community', icon: Users, label: L('Communauté', 'Community') },
    { key: 'profil', icon: UserCog, label: L('Profil', 'Profile') },
  ];

  return (
    <ClientSettingsProvider>
    <TourProvider>
      <SEOHead title={L('Mon espace | Body-Up', 'My area | Body-Up')} description="" path="/mon-espace" noindex />

      {/* Barre d'app minimaliste façon app native : juste le logo + accès rapide au profil */}
      <div className="sticky top-0 z-40 bg-black/95 backdrop-blur-md border-b border-white/10" style={{ paddingTop: 'env(safe-area-inset-top)' }} data-testid="app-topbar">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="app-logo-link">
            <img src={LOGO_URL} alt="Body-Up" className="h-7 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <motion.button
              whileTap={{ scale: 0.94 }}
              onClick={() => { setTab('profil'); setTimeout(() => document.getElementById('abonnement-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-zinc-200 bg-white/5 border border-white/10 hover:bg-white/10 hover:text-white transition-colors"
              data-testid="app-topbar-abonnement-btn"
            >
              <CreditCard className="w-3.5 h-3.5 text-primary" /> {L('Abonnement', 'Subscription')}
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setTab('profil')}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
              data-testid="app-topbar-profil-btn"
            >
              <UserCog className="w-4.5 h-4.5" />
            </motion.button>
          </div>
        </div>
      </div>

      <TrialEndingBanner en={en} onManage={() => { setTab('profil'); setTimeout(() => document.getElementById('abonnement-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60); }} />

      <main className="min-h-screen bg-black text-white px-4 sm:px-6 lg:px-8 pt-6 pb-32 lg:pb-20">
        <div className="max-w-5xl mx-auto">
          {/* Onglets (desktop uniquement — la barre du bas couvre mobile + tablette jusqu'à lg) */}
          <div className="hidden lg:flex gap-2 mb-8 bg-zinc-900/60 border border-white/10 p-1.5 rounded-2xl w-fit overflow-x-auto" data-testid="dashboard-tabs">
            {TABS.map((t) => (
              <motion.button
                key={t.key} onClick={() => setTab(t.key)} whileTap={{ scale: 0.94 }}
                className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${tab === t.key ? 'bg-gradient-to-r from-primary to-pink-500 text-white' : 'text-zinc-400 hover:text-white'}`}
                data-testid={`tab-${t.key}`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </motion.button>
            ))}
          </div>

          {/* Transition douce entre onglets (opacity uniquement — un `transform` sur cet ancestor
              casserait le positionnement `fixed` de TOUTES les modales des écrans enfants, cf. bug
              "Nouveau plan alimentaire" non cliquable). */}
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              {tab === 'tracking' && <TrackingDashboard en={en} onNavigate={goToTab} />}

              {tab === 'community' && (
                <CommunityHub en={en} onNavigate={goToTab} initialView={pendingCommunityView} onConsumeInitialView={() => setPendingCommunityView(null)} />
              )}

              {tab === 'workouts' && (
                <MyWorkouts
                  en={en} initialProgramId={pendingProgramId} initialSessionLabel={pendingSessionLabel}
                  onConsumeInitialProgramId={() => { setPendingProgramId(null); setPendingSessionLabel(null); }}
                />
              )}

              {tab === 'nutrition' && <NutritionHub en={en} />}

              {tab === 'profil' && (
                <ProfilTab en={en} toggleLanguage={toggleLanguage} canInstall={canInstall} onInstallClick={handleInstallClick} onLogout={() => { logout(); navigate('/', { replace: true }); }} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Barre de navigation mobile façon "dock" flottant — glass, pill active, ultra pro. Visible aussi sur tablette jusqu'à lg */}
      <nav
        className="lg:hidden fixed left-3 right-3 z-40 mx-auto max-w-md rounded-[26px] bg-zinc-950/95 backdrop-blur-2xl border border-white/10 shadow-[0_8px_28px_rgba(0,0,0,0.65)] flex items-stretch gap-1 p-1.5 transform-gpu"
        style={{ bottom: 'calc(0.75rem + env(safe-area-inset-bottom))', WebkitTransform: 'translateZ(0)' }}
        data-testid="mobile-tab-bar"
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <motion.button
              key={t.key} onClick={() => setTab(t.key)} whileTap={{ scale: 0.9 }}
              className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-1 py-2 px-0.5 rounded-2xl transition-all duration-300 ${
                active ? 'bg-gradient-to-b from-primary to-pink-600 shadow-md shadow-primary/40' : 'active:bg-white/5'
              }`}
              data-testid={`mobile-tab-${t.key}`}
            >
              <t.icon className={`w-5 h-5 shrink-0 transition-colors ${active ? 'text-white' : 'text-zinc-500'}`} strokeWidth={active ? 2.5 : 2} />
              <span className={`block w-full text-center truncate text-[9.5px] font-bold leading-tight transition-colors ${active ? 'text-white' : 'text-zinc-500'}`}>{t.mobileLabel || t.label}</span>
            </motion.button>
          );
        })}
      </nav>

      {/* AI Coach Chat - Floating Button (masqué sur l'onglet Communauté qui a déjà son propre chat, pour éviter toute superposition) */}
      <AICoachChat hidden={tab === 'community'} />
      <InstallAppBanner canInstall={canInstall} onInstallClick={handleInstallClick} en={en} />
      <IOSInstallModal open={iosModalOpen} onClose={() => setIosModalOpen(false)} L={L} />
      <TourSpotlight en={en} />
    </TourProvider>
    </ClientSettingsProvider>
  );
};

export default ClientDashboardPage;
